import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
    CalendarDays,
    ChevronLeft,
    ChevronRight,
    Search,
    Download,
    PlaneTakeoff,
    Clock,
    Receipt,
    HandCoins,
    Home as HomeIcon,
    MapPin,
    CalendarClock,
    Info,
    CheckCircle2,
    AlertCircle,
    X,
    Loader2,
    Sparkles,
    ArrowRight,
    ArrowLeftRight,
    LogIn,
    LogOut,
    Zap,
    ChevronsLeft,
    Scale,
    IndianRupee,
    MessageSquarePlus,
} from "lucide-react";
import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from "../../components/ui/sheet";
import { cn } from "../../utils/tailwindUtils";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { CALENDAR_EVENTS } from "../../constants/api";
import PenaltyAppealDialog from "../attendance/helpers/PenaltyAppealDialog";
import ShiftChangeRequestDialog from "../attendance/helpers/ShiftChangeRequestDialog";
import { canSubmitShiftChangeRequest, formatShiftTimeRange } from "../attendance/helpers/shiftChangeUtils";

const VIEW_MODES = ["Month", "Week", "Day"];
const WEEKDAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Where each quick / drawer action navigates.
const ROUTES = {
    leave: "/employee/leaverequest",
    wfh: "/employee/leaverequest",         // WFH lives inside the leave-request form
    on_duty: "/employee/leaverequest",     // same form, different leave_request_type
    reimbursement: "/employee/reimbursementrequest",
    advance: "/employee/advancerequest",
};

// Compute the [from, to] date range we need to load for a given view+cursor.
// Month view pads to start/end of week so the surrounding cells render too.
const computeRange = (cursor, view) => {
    if (view === "Month") {
        return {
            from: cursor.startOf("month").startOf("week").format("YYYY-MM-DD"),
            to: cursor.endOf("month").endOf("week").format("YYYY-MM-DD"),
        };
    }
    if (view === "Week") {
        return {
            from: cursor.startOf("week").format("YYYY-MM-DD"),
            to: cursor.endOf("week").format("YYYY-MM-DD"),
        };
    }
    return {
        from: cursor.format("YYYY-MM-DD"),
        to: cursor.format("YYYY-MM-DD"),
    };
};

const APPEAL_STATUS_CHIP = {
    pending: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
    waived: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300",
    reduced: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
    rejected: "bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300",
};

const penaltyMeta = (ev) => ev?.meta || {};

const STATUS_PILL = {
    pending: "bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-900/40 dark:text-orange-200 dark:border-orange-700",
    approved: "bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-200 dark:border-emerald-700",
    rejected: "bg-red-100 text-red-700 border-red-300 dark:bg-red-900/40 dark:text-red-200 dark:border-red-700",
    cancelled: "bg-gray-200 text-gray-700 border-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600",
};

export const EVENT_TYPES = {
    leave_approved: { key: "leave_approved", label: "Leave", color: "red", dot: "bg-red-500" },
    leave_pending: { key: "leave_pending", label: "Leave", color: "orange", dot: "bg-orange-500" },
    leave_rejected: { key: "leave_rejected", label: "Leave", color: "gray", dot: "bg-gray-400" },
    permission: { key: "permission", label: "Permission", color: "violet", dot: "bg-violet-500" },
    holiday: { key: "holiday", label: "Holiday", color: "green", dot: "bg-green-500" },
    weekoff: { key: "weekoff", label: "Week Off", color: "gray", dot: "bg-gray-400" },
    shift: { key: "shift", label: "Shift", color: "blue", dot: "bg-blue-500" },
    attendance: { key: "attendance", label: "Attendance", color: "teal", dot: "bg-teal-500" },
    overtime: { key: "overtime", label: "Overtime", color: "yellow", dot: "bg-yellow-500" },
    shift_change: { key: "shift_change", label: "Shift Change", color: "sky", dot: "bg-sky-500" },
    reimbursement: { key: "reimbursement", label: "Reimbursement", color: "cyan", dot: "bg-cyan-500" },
    advance: { key: "advance", label: "Advance Salary", color: "amber", dot: "bg-amber-500" },
    wfh: { key: "wfh", label: "Work From Home", color: "indigo", dot: "bg-indigo-500" },
    on_duty: { key: "on_duty", label: "On Duty", color: "pink", dot: "bg-pink-500" },
    training_assigned: { key: "training_assigned", label: "Training Assigned", color: "fuchsia", dot: "bg-fuchsia-500" },
    training_due: { key: "training_due", label: "Training Due", color: "rose", dot: "bg-rose-500" },
    penalty: { key: "penalty", label: "Fine / Deduction", color: "rose", dot: "bg-rose-600" },
};

export const eventTypeBadgeClass = (key) => {
    switch (key) {
        case "leave_approved": return "bg-red-50 text-red-700 border-red-200 dark:bg-red-900/25 dark:text-red-300 dark:border-red-800/60";
        case "leave_pending": return "bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/25 dark:text-orange-300 dark:border-orange-800/60";
        case "leave_rejected": return "bg-gray-50 text-gray-600 border-gray-200 dark:bg-gray-800/60 dark:text-gray-300 dark:border-gray-700";
        case "permission": return "bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-900/25 dark:text-violet-300 dark:border-violet-800/60";
        case "holiday": return "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/25 dark:text-green-300 dark:border-green-800/60";
        case "weekoff": return "bg-gray-50 text-gray-600 border-gray-200 dark:bg-gray-800/60 dark:text-gray-300 dark:border-gray-700";
        case "shift": return "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/25 dark:text-blue-300 dark:border-blue-800/60";
        case "attendance": return "bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-900/25 dark:text-teal-300 dark:border-teal-800/60";
        case "overtime": return "bg-yellow-50 text-yellow-800 border-yellow-200 dark:bg-yellow-900/25 dark:text-yellow-300 dark:border-yellow-800/60";
        case "shift_change": return "bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-900/25 dark:text-sky-300 dark:border-sky-800/60";
        case "reimbursement": return "bg-cyan-50 text-cyan-700 border-cyan-200 dark:bg-cyan-900/25 dark:text-cyan-300 dark:border-cyan-800/60";
        case "advance": return "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/25 dark:text-amber-300 dark:border-amber-800/60";
        case "wfh": return "bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-900/25 dark:text-indigo-300 dark:border-indigo-800/60";
        case "on_duty": return "bg-pink-50 text-pink-700 border-pink-200 dark:bg-pink-900/25 dark:text-pink-300 dark:border-pink-800/60";
        case "training_assigned": return "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200 dark:bg-fuchsia-900/25 dark:text-fuchsia-300 dark:border-fuchsia-800/60";
        case "training_due": return "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/25 dark:text-rose-300 dark:border-rose-800/60";
        case "penalty": return "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/25 dark:text-rose-300 dark:border-rose-800/60";
        default: return "bg-gray-50 text-gray-600 border-gray-200 dark:bg-gray-800/60 dark:text-gray-300 dark:border-gray-700";
    }
};

const isLeaveLike = (ev) =>
    Boolean(ev?.type) && (ev.type.startsWith("leave") || ev.type === "permission" || ev.type === "wfh");

const statusChipText = (status) => {
    if (!status) return null;
    if (status === "pending") return "Pending";
    if (status === "approved") return "Approved";
    if (status === "rejected") return "Rejected";
    if (status === "cancelled") return "Cancelled";
    if (status === "waived") return "Waived";
    if (status === "reduced") return "Reduced";
    if (status === "active") return null;
    return String(status).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const needsStatusBadge = (ev) =>
    Boolean(
        ev?.status &&
        STATUS_PILL[ev.status] &&
        (isLeaveLike(ev) || ev.type === "overtime" || ev.type === "shift_change")
    );

const EXPORT_STATUS_TYPES = new Set([
    "leave_approved",
    "leave_pending",
    "leave_rejected",
    "permission",
    "wfh",
    "on_duty",
    "overtime",
    "shift_change",
    "reimbursement",
    "advance",
    "penalty",
]);

const exportStatusLabel = (ev) => {
    if (ev?.type === "penalty") {
        const raw = ev.meta?.appeal?.status || (ev.status !== "active" ? ev.status : "");
        return statusChipText(raw) || "";
    }
    if (!EXPORT_STATUS_TYPES.has(ev?.type)) return "";
    return statusChipText(ev.status) || "";
};

const csvSanitize = (value) =>
    String(value ?? "")
        .replace(/\u20b9/g, "Rs.")
        .replace(/₹/g, "Rs.")
        .replace(/[\u2013\u2014]/g, "-");

/* ---- Attendance / leave meta helpers ---- */

// ISO timestamp -> "09:24". Returns null for empty/blank values so callers
// can decide what to render in its place (usually a dash).
const fmtTime = (iso) => {
    if (!iso) return null;
    const parsed = dayjs(iso);
    if (parsed.isValid()) return parsed.format("h:mm A");
    const asTime = dayjs(`1970-01-01T${String(iso).slice(0, 8)}`);
    return asTime.isValid() ? asTime.format("h:mm A") : null;
};

// Long label for the day drawer / day view.
const formatPeriod = (period) => {
    if (period === "first_half") return "First Half";
    if (period === "second_half") return "Second Half";
    if (period === "full_day") return "Full Day";
    return null;
};

const periodShort = (period) => {
    if (period === "first_half") return "½ 1st";
    if (period === "second_half") return "½ 2nd";
    if (period === "full_day") return "Full";
    return null;
};

// True when an attendance event carries shift/punch detail worth surfacing.
const hasAttendanceMeta = (ev) =>
    ev?.type === "attendance" && ev.meta &&
    (ev.meta.shift_check_in || ev.meta.check_in || ev.meta.check_out);

// Rich hover tooltip text for a pill — folds shift + punch + flags into one line.
const pillTitle = (ev) => {
    const bits = [ev.title];
    if (needsStatusBadge(ev)) bits.push(statusChipText(ev.status));
    if (ev.description) bits.push(`— ${ev.description}`);
    const m = ev.meta || {};
    if (m.shift_check_in && m.shift_check_out)
        bits.push(`| Shift ${fmtTime(m.shift_check_in)}–${fmtTime(m.shift_check_out)}`);
    if (m.check_in || m.check_out)
        bits.push(`| In ${fmtTime(m.check_in) || "—"} · Out ${fmtTime(m.check_out) || "—"}`);
    if (m.is_short_duty) bits.push("| Short duty");
    if (m.is_auto_marked) bits.push("| Auto-marked");
    if (ev.type === "penalty") {
        const pm = penaltyMeta(ev);
        bits.push(`| ${pm.penalty_label || "Penalty"}: ${pm.value_display || ev.title}`);
        if (pm.appeal?.status_label) bits.push(`| Appeal ${pm.appeal.status_label}`);
    }
    if (isLeaveLike(ev) && formatPeriod(m.period))
        bits.push(`| ${formatPeriod(m.period)}`);
    if (isLeaveLike(ev) && m.total_days)
        bits.push(`| ${m.total_days} day${Number(m.total_days) === 1 ? "" : "s"}`);
    if (ev.type === "permission" && (m.start_time || m.end_time))
        bits.push(`| ${fmtTime(m.start_time) || "—"}–${fmtTime(m.end_time) || "—"}`);
    if (ev.type === "overtime" && m.hours_label) bits.push(`| ${m.hours_label}`);
    if (ev.type === "shift_change" && (m.from_shift || m.to_shift))
        bits.push(`| ${m.from_shift || "—"} → ${m.to_shift || "—"}`);
    return bits.join(" ");
};

// Multi-day events are returned as one row with start_date/end_date.
// Fan them out into a per-day map so the calendar grid can render them in each cell.
const buildEventsByDate = (events) => {
    const map = {};
    for (const ev of events) {
        const start = dayjs(ev.start_date);
        const end = dayjs(ev.end_date || ev.start_date);
        let d = start;
        while (d.isBefore(end) || d.isSame(end, "day")) {
            const key = d.format("YYYY-MM-DD");
            (map[key] = map[key] || []).push(ev);
            d = d.add(1, "day");
        }
    }
    return map;
};

/* =====================================================
   ROOT
===================================================== */
const Calendar = () => {
    const { fetch } = useApiMethods();
    const navigate = useNavigate();

    const goTo = (key, extra = {}) => {
        const path = ROUTES[key];
        if (!path) return;
        navigate(path, { state: { fromCalendar: true, ...extra } });
    };

    const readStoredView = () => {
        try {
            const fromUrl = new URLSearchParams(window.location.search).get("view");
            if (VIEW_MODES.includes(fromUrl)) return fromUrl;
            const stored = sessionStorage.getItem("hrzyra.calendar.view");
            return VIEW_MODES.includes(stored) ? stored : "Month";
        } catch {
            return "Month";
        }
    };
    const readStoredCursor = () => {
        try {
            const fromUrl = new URLSearchParams(window.location.search).get("cursor");
            const stored = sessionStorage.getItem("hrzyra.calendar.cursor");
            const parsed = dayjs(fromUrl || stored || undefined);
            return parsed.isValid() ? parsed : dayjs();
        } catch {
            return dayjs();
        }
    };

    const [cursor, setCursor] = useState(readStoredCursor);
    const [view, setView] = useState(readStoredView);
    const [selectedDate, setSelectedDate] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [panelOpen, setPanelOpen] = useState(false);

    // Filters
    const [search, setSearch] = useState("");
    const [departmentId] = useState(null);
    const [employeeId] = useState(null);
    const [typeFilters] = useState([]);

    // API state
    const [events, setEvents] = useState([]);
    const [eventsRange, setEventsRange] = useState(null);
    const [summary, setSummary] = useState({ leave_balance: null, pending_count: 0 });
    const [loading, setLoading] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const [appealOpen, setAppealOpen] = useState(false);
    const [appealPenalty, setAppealPenalty] = useState(null);
    const [shiftChangeOpen, setShiftChangeOpen] = useState(false);
    const [shiftChangeContext, setShiftChangeContext] = useState(null);
    const canSubmitShiftChange = canSubmitShiftChangeRequest();

    // ---------- Load events from API whenever range/scope changes ----------
    const { from, to } = useMemo(() => computeRange(cursor, view), [cursor, view]);

    useEffect(() => {
        const dateKey = cursor.format("YYYY-MM-DD");
        try {
            sessionStorage.setItem("hrzyra.calendar.view", view);
            sessionStorage.setItem("hrzyra.calendar.cursor", dateKey);
        } catch {
            /* ignore quota / private-mode */
        }
        const params = new URLSearchParams();
        params.set("view", view);
        params.set("cursor", dateKey);
        const next = `/calendar?${params.toString()}`;
        if (`${window.location.pathname}${window.location.search}` !== next) {
            navigate(next, { replace: true });
        }
    }, [view, cursor, navigate]);

    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key === "Escape" && drawerOpen) {
                setDrawerOpen(false);
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [drawerOpen]);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            try {
                const params = { from, to };
                if (departmentId) params.department_id = departmentId;
                if (employeeId) params.employee_id = employeeId;
                if (typeFilters.length) params.types = typeFilters.join(",");

                const response = await fetch(CALENDAR_EVENTS, params);
                if (cancelled) return;

                if (response?.status === "success") {
                    setEvents(response.data?.events || []);
                    setEventsRange({ from, to });
                    setSummary(response.data?.summary || { leave_balance: null, pending_count: 0 });
                } else {
                    ToastMsg("error", response?.message || "Failed to load calendar.");
                    setEvents([]);
                    setEventsRange({ from, to });
                }
            } catch (err) {
                if (!cancelled) {
                    ToastMsg("error", err?.message || "Failed to load calendar.");
                    setEvents([]);
                    setEventsRange({ from, to });
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [from, to, departmentId, employeeId, typeFilters, reloadKey]);

    const openAppeal = (penaltyRow) => {
        setAppealPenalty(penaltyRow);
        setAppealOpen(true);
    };

    const handleAppealSuccess = () => {
        setReloadKey((k) => k + 1);
    };

    // ---------- Client-side search filter ----------
    const filteredEvents = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return events;
        return events.filter((ev) => {
            const hay = `${ev.title} ${ev.description || ""}`.toLowerCase();
            return hay.includes(q);
        });
    }, [events, search]);

    const eventsByDate = useMemo(() => buildEventsByDate(filteredEvents), [filteredEvents]);

    const pendingRequests = useMemo(
        () => filteredEvents.filter((e) => e.status === "pending"),
        [filteredEvents]
    );

    // ---------- Navigation ----------
    const goPrev = () => {
        if (view === "Month") setCursor((c) => c.subtract(1, "month"));
        else if (view === "Week") setCursor((c) => c.subtract(1, "week"));
        else setCursor((c) => c.subtract(1, "day"));
    };
    const goNext = () => {
        if (view === "Month") setCursor((c) => c.add(1, "month"));
        else if (view === "Week") setCursor((c) => c.add(1, "week"));
        else setCursor((c) => c.add(1, "day"));
    };
    const goToday = () => setCursor(dayjs());

    const openDate = (date) => {
        setSelectedDate(date);
        setDrawerOpen(true);
    };

    const openShiftChangeRequest = (date, events = []) => {
        const attendance = events.find((e) => e.type === "attendance");
        const meta = attendance?.meta || {};
        const shiftLabel = attendance?.title
            || formatShiftTimeRange(meta.shift_check_in, meta.shift_check_out)
            || meta.shift_name
            || null;

        setShiftChangeContext({
            date: date.format("YYYY-MM-DD"),
            currentShiftLabel: shiftLabel,
            hasAttendance: Boolean(attendance),
        });
        setShiftChangeOpen(true);
    };

    // ---------- Header label ----------
    const headerLabel = useMemo(() => {
        if (view === "Month") return cursor.format("MMMM YYYY");
        if (view === "Week") {
            const start = cursor.startOf("week");
            const end = cursor.endOf("week");
            const sameMonth = start.month() === end.month();
            return sameMonth
                ? `${start.format("MMM D")} – ${end.format("D, YYYY")}`
                : `${start.format("MMM D")} – ${end.format("MMM D, YYYY")}`;
        }
        return cursor.format("dddd, MMM D, YYYY");
    }, [cursor, view]);

    const isTodayInView = useMemo(() => {
        const t = dayjs();
        if (view === "Month") return t.isSame(cursor, "month");
        if (view === "Week") return t.isSame(cursor, "week");
        return t.isSame(cursor, "day");
    }, [cursor, view]);

    const exportReady = !loading && eventsRange?.from === from && eventsRange?.to === to;

    // ---------- Export ----------
    const handleExport = () => {
        if (!exportReady) return;
        const esc = (v) => `"${csvSanitize(v).replace(/"/g, '""')}"`;
        const header = "Start,End,Type,Title,Description,Status\n";
        const rows = filteredEvents.map((e) =>
            [
                e.start_date,
                e.end_date,
                EVENT_TYPES[e.type]?.label || e.type,
                esc(e.title),
                esc(e.description),
                esc(exportStatusLabel(e)),
            ].join(",")
        );
        const csv = `\uFEFF${header}${rows.join("\n")}`;
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `calendar-${cursor.format("YYYY-MM")}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <ContainerCard scrollable>
            <div className="p-2 flex justify-between items-center">
                <ModuleTitleCard
                    Icon={CalendarDays}
                    title="Calendar"
                    subtitle="Centralized view of leaves, shifts, holidays & more"
                />

                <LeaveCard leaveBalance={summary.leave_balance} pendingCount={pendingRequests.length} onExpand={() => setPanelOpen(true)} />
            </div>

            <div className="px-3 pb-4 space-y-3">
                {/* ---------- Toolbar ---------- */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-3 flex flex-wrap items-center gap-3">
                    {/* Date navigation */}
                    <div className="flex items-center gap-1">
                        <button
                            onClick={goPrev}
                            className="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors"
                            aria-label="Previous"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button
                            onClick={goToday}
                            disabled={isTodayInView}
                            className={cn(
                                "h-9 px-3 rounded-lg text-sm font-medium border transition-colors",
                                isTodayInView
                                    ? "border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500 cursor-default"
                                    : "border-sidebar-primary/30 text-sidebar-primary hover:bg-sidebar-primary/10"
                            )}
                        >
                            Today
                        </button>
                        <button
                            onClick={goNext}
                            className="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors"
                            aria-label="Next"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>

                    {/* Current period label */}
                    <div className="flex items-center gap-2 min-w-[180px]">
                        <h2 className="text-xl font-bold tracking-tight text-gray-800 dark:text-gray-100">
                            {headerLabel}
                        </h2>
                        {loading && <Loader2 className="w-4 h-4 animate-spin text-sidebar-primary" />}
                    </div>

                    {/* View switcher */}
                    <div className="flex items-center bg-gray-100 dark:bg-gray-900/70 rounded-xl p-1 ml-auto">
                        {VIEW_MODES.map((m) => (
                            <button
                                key={m}
                                onClick={() => setView(m)}
                                className={cn(
                                    "px-4 py-1.5 text-sm font-medium rounded-lg transition-all",
                                    view === m
                                        ? "bg-background text-sidebar-primary shadow-sm"
                                        : "text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                                )}
                            >
                                {m}
                            </button>
                        ))}
                    </div>

                    {/* Search */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search events…"
                            className="h-9 pl-9 pr-3 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-sidebar-primary/40 focus:border-sidebar-primary text-gray-800 dark:text-gray-200 w-44 transition-shadow"
                        />
                    </div>

                    <button
                        type="button"
                        onClick={handleExport}
                        disabled={!exportReady}
                        title={exportReady ? "Export this period" : "Wait until this period has loaded"}
                        className={cn(
                            "h-9 px-3 inline-flex items-center gap-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-600 dark:text-gray-300 transition-colors",
                            exportReady
                                ? "hover:bg-gray-50 dark:hover:bg-gray-700/60"
                                : "opacity-50 cursor-not-allowed"
                        )}
                    >
                        <Download className="w-4 h-4" /> Export
                    </button>
                </div>

                {/* ---------- Grid + Sidebar ---------- */}
                <div className="grid grid-cols-1 xl:grid-cols-4 gap-3">
                    <div className="xl:col-span-4 space-y-3">
                        {view === "Month" && (
                            <MonthView cursor={cursor} eventsByDate={eventsByDate} onDateClick={openDate} loading={loading} />
                        )}
                        {view === "Week" && (
                            <WeekView cursor={cursor} eventsByDate={eventsByDate} onDateClick={openDate} loading={loading} />
                        )}
                        {view === "Day" && (
                            <DayView cursor={cursor} eventsByDate={eventsByDate} onRaiseConcern={openAppeal} loading={loading} />
                        )}

                        {/* Legend */}
                        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm px-4 py-3">
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                                {Object.values(EVENT_TYPES).map((t) => (
                                    <div key={t.key} className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                                        <span className={cn("w-2.5 h-2.5 rounded-full ring-2 ring-white dark:ring-gray-800", t.dot)} />
                                        {t.label}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
                {drawerOpen && (
                    <SheetContent side="right" hideClose className="w-full sm:max-w-[420px] p-0 overflow-hidden flex flex-col">
                        <DateDrawer
                            date={selectedDate}
                            events={selectedDate ? eventsByDate[selectedDate.format("YYYY-MM-DD")] || [] : []}
                            onClose={() => setDrawerOpen(false)}
                            goTo={goTo}
                            onRaiseConcern={openAppeal}
                            canSubmitShiftChange={canSubmitShiftChange}
                            onShiftChangeRequest={openShiftChangeRequest}
                        />
                    </SheetContent>
                )}
            </Sheet>

            {/* Side panel in a right drawer (opened from the LeaveCard chevron) */}
            <Sheet open={panelOpen} onOpenChange={setPanelOpen}>
                {panelOpen && (
                    <SheetContent side="right" className="w-full sm:max-w-md overflow-auto">
                        <SheetHeader className="mb-3">
                            <SheetTitle>Overview</SheetTitle>
                            <SheetDescription>Leave balance, this month & requests</SheetDescription>
                        </SheetHeader>
                        <div className="space-y-3">
                            <SidePanel
                                cursor={cursor}
                                leaveBalance={summary.leave_balance}
                                pendingRequests={pendingRequests}
                                events={filteredEvents}
                                onNavigateDate={(d) => { setPanelOpen(false); openDate(d); }}
                                goTo={goTo}
                            />
                        </div>
                    </SheetContent>
                )}
            </Sheet>

            <PenaltyAppealDialog
                open={appealOpen}
                penalty={appealPenalty}
                onClose={() => setAppealOpen(false)}
                onSuccess={handleAppealSuccess}
            />

            <ShiftChangeRequestDialog
                open={shiftChangeOpen}
                onClose={() => setShiftChangeOpen(false)}
                onSuccess={() => setReloadKey((k) => k + 1)}
                initialDate={shiftChangeContext?.date}
                lockDate
                currentShiftLabel={shiftChangeContext?.currentShiftLabel}
                hasAttendance={shiftChangeContext?.hasAttendance !== false}
            />
        </ContainerCard>
    );
};

/* =====================================================
   EVENT PILL (shared, month + week)
===================================================== */
const EventPill = ({ ev, dense }) => {
    const t = EVENT_TYPES[ev.type];
    const m = ev.meta || {};
    const pm = penaltyMeta(ev);
    const shortDuty = ev.type === "attendance" && m.is_short_duty;
    const autoMarked = ev.type === "attendance" && m.is_auto_marked;
    const isPenalty = ev.type === "penalty";
    const half = isLeaveLike(ev) ? periodShort(m.period) : null;
    const statusText = needsStatusBadge(ev) ? statusChipText(ev.status) : null;
    return (
        <div
            title={pillTitle(ev)}
            className={cn(
                "flex items-center gap-1.5 rounded-md border transition-colors",
                dense ? "px-1.5 py-0.5 text-[11px]" : "px-2 py-1 text-xs",
                eventTypeBadgeClass(ev.type),
                needsStatusBadge(ev) && ev.status === "pending" && "border-dashed"
            )}
        >
            <span className={cn("w-1.5 h-1.5 rounded-full shrink-0", t?.dot)} />
            <span className="truncate font-medium min-w-0">{ev.title}</span>
            {statusText && (
                <span
                    className={cn(
                        "shrink-0 rounded px-1 text-[9px] font-bold uppercase leading-none border",
                        STATUS_PILL[ev.status]
                    )}
                >
                    {statusText}
                </span>
            )}
            {isPenalty && (
                <span
                    title={pm.penalty_label || "Penalty"}
                    className="shrink-0 inline-flex items-center gap-0.5 rounded px-1 text-[9px] font-bold uppercase leading-none bg-rose-200/80 text-rose-800 dark:bg-rose-800/60 dark:text-rose-100"
                >
                    {pm.penalty_mode === "fine" ? <IndianRupee className="w-2 h-2" /> : <Scale className="w-2 h-2" />}
                    {pm.penalty_mode === "fine" ? "Fine" : "LOP"}
                </span>
            )}
            {shortDuty && (
                <span
                    title="Short duty"
                    className="shrink-0 inline-flex items-center gap-0.5 rounded px-1 text-[9px] font-bold uppercase leading-none bg-amber-200/80 text-amber-800 dark:bg-amber-800/60 dark:text-amber-100"
                >
                    <Zap className="w-2 h-2" />SD
                </span>
            )}
            {half && (
                <span
                    title={formatPeriod(m.period) || "Leave duration"}
                    className="shrink-0 rounded px-1 text-[9px] font-bold leading-none bg-violet-200/80 text-violet-800 dark:bg-violet-800/60 dark:text-violet-100"
                >
                    {half}
                </span>
            )}
            {autoMarked && (
                <span
                    title="Auto-marked"
                    className="shrink-0 rounded px-1 text-[9px] font-bold uppercase leading-none bg-slate-200/80 text-slate-700 dark:bg-slate-700/70 dark:text-slate-200"
                >
                    A
                </span>
            )}
        </div>
    );
};

/* =====================================================
   EVENT META (shift + punch detail under a pill)
===================================================== */
const EventMeta = ({ ev, className }) => {
    const m = ev?.meta;
    if (!m) return null;

    const shiftIn = fmtTime(m.shift_check_in);
    const shiftOut = fmtTime(m.shift_check_out);
    const inT = fmtTime(m.check_in);
    const outT = fmtTime(m.check_out);
    const period = isLeaveLike(ev) ? formatPeriod(m.period) : null;
    const permIn = ev.type === "permission" ? fmtTime(m.start_time) : null;
    const permOut = ev.type === "permission" ? fmtTime(m.end_time) : null;
    const hasPermissionTime = Boolean(permIn || permOut);
    const otHours = ev.type === "overtime" ? m.hours_label : null;
    const shiftChange = ev.type === "shift_change" && (m.from_shift || m.to_shift);

    const hasShift = shiftIn && shiftOut;
    const hasPunch = inT || outT;
    if (!hasShift && !hasPunch && !period && !hasPermissionTime && !otHours && !shiftChange) return null;

    return (
        <div className={cn("space-y-0.5 text-[10px] leading-tight text-gray-500 dark:text-gray-400", className)}>
            {hasShift && (
                <div className="flex items-center gap-1">
                    <Clock className="w-2.5 h-2.5 shrink-0 opacity-70" />
                    <span>{shiftIn}–{shiftOut}</span>
                </div>
            )}
            {hasPunch && (
                <div className="flex items-center gap-1">
                    <LogIn className="w-2.5 h-2.5 shrink-0 opacity-70" />
                    <span>In {inT || "—"} · Out {outT || "—"}</span>
                </div>
            )}
            {hasPermissionTime && (
                <div className="flex items-center gap-1">
                    <Clock className="w-2.5 h-2.5 shrink-0 opacity-70" />
                    <span>{permIn || "—"}–{permOut || "—"}</span>
                </div>
            )}
            {period && (
                <div className="flex items-center gap-1">
                    <span className="opacity-70">Period:</span>
                    <span className="font-medium">{period}</span>
                </div>
            )}
            {otHours && (
                <div className="flex items-center gap-1">
                    <Clock className="w-2.5 h-2.5 shrink-0 opacity-70" />
                    <span>{otHours}</span>
                </div>
            )}
            {shiftChange && (
                <div className="flex items-center gap-1">
                    <ArrowLeftRight className="w-2.5 h-2.5 shrink-0 opacity-70" />
                    <span>{m.from_shift || "—"} → {m.to_shift || "—"}</span>
                </div>
            )}
        </div>
    );
};

// One-line in/out punch row that spreads across the full cell width.
// Renders nothing when there are no punches (week-offs, holidays).
const PunchRow = ({ ev, className }) => {
    const m = ev?.meta;
    if (!m) return null;
    const inT = fmtTime(m.check_in);
    const outT = fmtTime(m.check_out);
    if (!inT && !outT) return null;
    return (
        <div className={cn("flex items-center justify-between text-[10px] text-gray-500 dark:text-gray-400", className)}>
            <span className="inline-flex items-center gap-1">
                <LogIn className="w-2.5 h-2.5 shrink-0 text-emerald-500/80" />
                {inT || "—"}
            </span>
            <span className="inline-flex items-center gap-1">
                <LogOut className="w-2.5 h-2.5 shrink-0 text-rose-400/80" />
                {outT || "—"}
            </span>
        </div>
    );
};

/* =====================================================
   MONTH VIEW
===================================================== */
const MonthView = ({ cursor, eventsByDate, onDateClick, loading }) => {
    const gridStart = cursor.startOf("month").startOf("week");
    const gridEnd = cursor.endOf("month").endOf("week");
    const today = dayjs();

    const days = [];
    let d = gridStart;
    while (d.isBefore(gridEnd) || d.isSame(gridEnd, "day")) {
        days.push(d);
        d = d.add(1, "day");
    }

    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden relative">
            {loading && (
                <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 z-10 flex items-center justify-center pointer-events-none backdrop-blur-[1px]">
                    <Loader2 className="w-6 h-6 animate-spin text-sidebar-primary" />
                </div>
            )}

            {/* Weekday header */}
            <div className="grid grid-cols-7 bg-gray-50/80 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                {WEEKDAYS_SHORT.map((w, i) => (
                    <div
                        key={w}
                        className={cn(
                            "px-3 py-2.5 text-center text-[11px] font-semibold uppercase tracking-wider",
                            i === 0 || i === 6 ? "text-sidebar-primary/70" : "text-gray-400 dark:text-gray-500"
                        )}
                    >
                        {w}
                    </div>
                ))}
            </div>

            {/* Day grid */}
            <div className="grid grid-cols-7 auto-rows-fr">
                {days.map((day, idx) => {
                    const dateKey = day.format("YYYY-MM-DD");
                    const events = eventsByDate[dateKey] || [];
                    const inMonth = day.month() === cursor.month();
                    const isToday = day.isSame(today, "day");
                    const isWeekend = day.day() === 0 || day.day() === 6;
                    const lastCol = (idx + 1) % 7 === 0;

                    // Primary attendance event drives the shift time shown in
                    // the header (frees the crowded space under the pill).
                    const attnEvent = events.find((e) => e.type === "attendance");
                    const shiftIn = fmtTime(attnEvent?.meta?.shift_check_in);
                    const shiftOut = fmtTime(attnEvent?.meta?.shift_check_out);
                    const showShift = shiftIn && shiftOut;

                    return (
                        <button
                            key={dateKey}
                            onClick={() => onDateClick(day)}
                            className={cn(
                                "group min-h-[124px] text-left p-2.5 border-b border-gray-100 dark:border-gray-700/50 transition-colors focus:outline-none focus:ring-2 focus:ring-sidebar-primary/40 focus:ring-inset flex flex-col gap-1.5",
                                !lastCol && "border-r",
                                !inMonth
                                    ? "bg-gray-50/60 dark:bg-gray-900/40"
                                    : isToday
                                        ? "bg-sidebar-primary/5"
                                        : isWeekend
                                            ? "bg-gray-50/40 dark:bg-gray-900/20"
                                            : "bg-white dark:bg-gray-800",
                                "hover:bg-sidebar-primary/[0.06] dark:hover:bg-gray-900/60"
                            )}
                        >
                            {/* Header: date + (shift time | event count) */}
                            <div className="flex items-center justify-between">
                                <span
                                    className={cn(
                                        "inline-flex items-center justify-center text-xs font-semibold w-7 h-7 rounded-full transition-colors",
                                        isToday
                                            ? "bg-sidebar-primary text-white shadow-sm"
                                            : inMonth
                                                ? "text-gray-700 dark:text-gray-200 group-hover:bg-white dark:group-hover:bg-gray-700"
                                                : "text-gray-400 dark:text-gray-600"
                                    )}
                                >
                                    {day.date()}
                                </span>
                                {showShift ? (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-medium text-gray-400 dark:text-gray-500 tabular-nums">
                                        <Clock className="w-3 h-3 opacity-70" />
                                        {shiftIn}–{shiftOut}
                                    </span>
                                ) : events.length > 1 ? (
                                    <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700/60 rounded-full px-1.5 py-0.5 leading-none">
                                        {events.length}
                                    </span>
                                ) : null}
                            </div>

                            {/* Body: status pill(s) + punch row */}
                            <div className="flex-1 min-h-0 overflow-y-auto space-y-1.5 max-h-[9.5rem] pr-0.5">
                                {events.map((ev, i) => (
                                    <div key={`${ev.id}-${i}`} className="space-y-1">
                                        <EventPill ev={ev} dense />
                                        {ev.type === "attendance" && <PunchRow ev={ev} className="px-0.5" />}
                                    </div>
                                ))}
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

/* =====================================================
   WEEK VIEW
===================================================== */
const WeekView = ({ cursor, eventsByDate, onDateClick, loading }) => {
    const start = cursor.startOf("week");
    const today = dayjs();
    const days = Array.from({ length: 7 }, (_, i) => start.add(i, "day"));

    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden relative">
            {loading && (
                <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 z-10 flex items-center justify-center pointer-events-none backdrop-blur-[1px]">
                    <Loader2 className="w-6 h-6 animate-spin text-sidebar-primary" />
                </div>
            )}
            <div className="grid grid-cols-7 border-b border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/50">
                {days.map((day) => {
                    const isToday = day.isSame(today, "day");
                    return (
                        <div key={day.format("YYYY-MM-DD")} className="p-3 text-center border-r border-gray-100 dark:border-gray-700/50 last:border-r-0">
                            <div className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">{day.format("ddd")}</div>
                            <div className={cn(
                                "mt-1 inline-flex items-center justify-center w-9 h-9 rounded-full text-sm font-bold transition-colors",
                                isToday ? "bg-sidebar-primary text-white shadow-sm" : "text-gray-700 dark:text-gray-100"
                            )}>
                                {day.date()}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="grid grid-cols-7 min-h-[520px]">
                {days.map((day) => {
                    const events = eventsByDate[day.format("YYYY-MM-DD")] || [];
                    const isToday = day.isSame(today, "day");
                    return (
                        <button
                            key={day.format("YYYY-MM-DD") + "col"}
                            onClick={() => onDateClick(day)}
                            className={cn(
                                "text-left p-2 border-r border-gray-100 dark:border-gray-700/50 last:border-r-0 hover:bg-sidebar-primary/[0.06] dark:hover:bg-gray-900/60 transition-colors space-y-1.5 align-top overflow-y-auto max-h-[520px]",
                                isToday && "bg-sidebar-primary/5"
                            )}
                        >
                            {events.length === 0 && (
                                <div className="text-[11px] text-gray-300 dark:text-gray-600 italic pt-2 text-center">—</div>
                            )}
                            {events.map((ev, i) => (
                                <div key={`${ev.id}-${i}`}>
                                    <EventPill ev={ev} />
                                    {ev.description && (
                                        <div className="text-[10px] text-gray-400 dark:text-gray-500 truncate mt-0.5 px-1">{ev.description}</div>
                                    )}
                                    {hasAttendanceMeta(ev) && <EventMeta ev={ev} className="mt-0.5 px-1" />}
                                </div>
                            ))}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

/* =====================================================
   DAY VIEW (agenda)
===================================================== */
const DayView = ({ cursor, eventsByDate, onRaiseConcern, loading = false }) => {
    const events = eventsByDate[cursor.format("YYYY-MM-DD")] || [];
    const isToday = cursor.isSame(dayjs(), "day");

    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 min-h-[560px]">
            <div className="flex items-center justify-between mb-5 pb-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-4">
                    <div className={cn(
                        "flex flex-col items-center justify-center w-16 h-16 rounded-2xl shrink-0",
                        isToday ? "bg-sidebar-primary text-white" : "bg-gray-100 dark:bg-gray-900 text-gray-700 dark:text-gray-200"
                    )}>
                        <span className="text-[10px] font-semibold uppercase tracking-wider opacity-80">{cursor.format("MMM")}</span>
                        <span className="text-2xl font-bold leading-none">{cursor.format("D")}</span>
                    </div>
                    <div>
                        <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100">
                            {cursor.format("dddd")}
                            {isToday && <span className="ml-2 align-middle text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full bg-sidebar-primary/10 text-sidebar-primary">Today</span>}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{cursor.format("MMMM D, YYYY")}</p>
                    </div>
                </div>
                <div className="text-right">
                    <div className="text-3xl font-bold text-sidebar-primary leading-none">{events.length}</div>
                    <div className="text-[11px] text-gray-400 dark:text-gray-500 uppercase tracking-wider mt-1">Events</div>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-16 text-gray-400 dark:text-gray-500">
                    <Loader2 className="w-10 h-10 mx-auto mb-3 animate-spin opacity-60" />
                    <p className="text-sm">Loading events…</p>
                </div>
            ) : events.length === 0 ? (
                <div className="text-center py-16 text-gray-400 dark:text-gray-500">
                    <CalendarDays className="w-14 h-14 mx-auto mb-3 opacity-40" />
                    <p className="text-sm">No events scheduled for this day</p>
                </div>
            ) : (
                <div className="space-y-2.5">
                    {events.map((ev, i) => {
                        const t = EVENT_TYPES[ev.type];
                        return (
                            <div
                                key={`${ev.id}-${i}`}
                                className={cn(
                                    "flex items-start gap-3 p-3.5 rounded-xl border",
                                    eventTypeBadgeClass(ev.type),
                                    needsStatusBadge(ev) && ev.status === "pending" && "ring-1 ring-orange-300/70 dark:ring-orange-600/50"
                                )}
                            >
                                <span className={cn("w-2.5 h-2.5 rounded-full mt-1.5 shrink-0 ring-2 ring-white/60 dark:ring-black/20", t?.dot)} />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-2">
                                        <h4 className="font-semibold text-sm flex items-center gap-2">
                                            {ev.title}
                                            {needsStatusBadge(ev) && (
                                                <span className={cn(
                                                    "text-[10px] uppercase font-bold tracking-wide px-1.5 py-0.5 rounded-full border",
                                                    STATUS_PILL[ev.status]
                                                )}>
                                            {statusChipText(ev.status)}
                                                </span>
                                            )}
                                        </h4>
                                        <span className="text-[11px] uppercase tracking-wide font-medium opacity-70 shrink-0">{t?.label}</span>
                                    </div>
                                    {ev.description && (
                                        <p className="text-xs mt-1 opacity-80">{ev.description}</p>
                                    )}
                                    <EventMeta ev={ev} className="mt-1.5" />
                                    <AttendanceDetail ev={ev} className="mt-2" />
                                    <PenaltyDetail ev={ev} className="mt-2" onRaiseConcern={onRaiseConcern} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const PenaltyDetail = ({ ev, className, onRaiseConcern }) => {
    const pm = penaltyMeta(ev);
    if (ev?.type !== "penalty") return null;

    const appeal = pm.appeal;
    const canAppeal = pm.can_appeal;

    return (
        <div className={cn("space-y-2", className)}>
            <div className="flex flex-wrap items-center gap-2 text-[11px]">
                <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-semibold bg-rose-100 text-rose-700 border-rose-300 dark:bg-rose-900/40 dark:text-rose-200 dark:border-rose-700">
                    {pm.penalty_mode === "fine" ? <IndianRupee className="w-3 h-3" /> : <Scale className="w-3 h-3" />}
                    {pm.value_display}
                </span>
                <span className="text-gray-500 dark:text-gray-400">{pm.penalty_type}</span>
                <span className="text-gray-400">· {pm.review_status}</span>
            </div>
            {appeal && (
                <span className={cn("inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium", APPEAL_STATUS_CHIP[appeal.status] || APPEAL_STATUS_CHIP.pending)}>
                    Appeal: {appeal.status_label}
                </span>
            )}
            {canAppeal && onRaiseConcern && (
                <Button
                    variant="outline"
                    size="sm"
                    className="h-8"
                    onClick={() => onRaiseConcern(pm)}
                >
                    <MessageSquarePlus className="w-4 h-4 mr-1.5" />
                    Raise concern
                </Button>
            )}
        </div>
    );
};

/* =====================================================
   ATTENDANCE DETAIL (expanded — day view + drawer)
===================================================== */
const AttendanceDetail = ({ ev, className }) => {
    const m = ev?.meta;
    if (!m) return null;

    const shiftIn = fmtTime(m.shift_check_in);
    const shiftOut = fmtTime(m.shift_check_out);
    const inT = fmtTime(m.check_in);
    const outT = fmtTime(m.check_out);
    const period = isLeaveLike(ev) ? formatPeriod(m.period) : null;

    const rows = [];
    if (ev.type === "attendance") {
        if (shiftIn && shiftOut) rows.push(["Shift", `${shiftIn} – ${shiftOut}`]);
        if (inT || outT) rows.push(["Check-in / out", `${inT || "—"} · ${outT || "—"}`]);
    }
    if (period) rows.push(["Period", period]);
    if (isLeaveLike(ev) && m.total_days) {
        rows.push(["Duration", `${m.total_days} day${Number(m.total_days) === 1 ? "" : "s"}`]);
    }
    if (ev.type === "permission" && (m.start_time || m.end_time)) {
        rows.push(["Time", `${fmtTime(m.start_time) || "—"} – ${fmtTime(m.end_time) || "—"}`]);
    }

    const chips = [];
    if (ev.type === "attendance" && m.is_short_duty) chips.push(["Short Duty", "amber"]);
    if (ev.type === "attendance" && m.is_auto_marked) chips.push(["Auto-marked", "slate"]);
    if (ev.type === "attendance" && m.short_duty_reviewed && (m.short_duty_fine > 0 || m.short_duty_deduction > 0)) {
        const val = m.short_duty_mode === "fine"
            ? `₹${Number(m.short_duty_fine || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : `${m.short_duty_deduction} day LOP`;
        chips.push([val, "rose"]);
    }

    if (!rows.length && !chips.length) return null;

    const chipTone = {
        amber: "bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/40 dark:text-amber-200 dark:border-amber-700",
        slate: "bg-slate-100 text-slate-600 border-slate-300 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-600",
        rose: "bg-rose-100 text-rose-700 border-rose-300 dark:bg-rose-900/40 dark:text-rose-200 dark:border-rose-700",
    };

    return (
        <div className={cn("space-y-1.5", className)}>
            {rows.map(([label, value]) => (
                <div key={label} className="flex items-center gap-2 text-[11px]">
                    <span className="uppercase tracking-wide opacity-60 w-24 shrink-0">{label}</span>
                    <span className="font-medium tabular-nums">{value}</span>
                </div>
            ))}
            {chips.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-0.5">
                    {chips.map(([label, tone]) => (
                        <span key={label} className={cn("inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full border", chipTone[tone])}>
                            {label === "Short Duty" && <Zap className="w-2.5 h-2.5" />}
                            {label}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

/* =====================================================
   SIDE PANEL
===================================================== */
const LeaveCard = ({ leaveBalance, pendingCount = 0, onExpand }) => {
    const balanceValue = useMemo(() => {
        if (leaveBalance == null) return null;
        if (typeof leaveBalance === "number") return leaveBalance;
        if (typeof leaveBalance === "object") {
            const c = leaveBalance.balance ?? leaveBalance.available_leaves ?? leaveBalance.available;
            if (typeof c === "number") return c;
        }
        return null;
    }, [leaveBalance]);

    return (
        <div className="rounded-2xl border border-sidebar-primary/20 shadow-sm bg-gradient-to-br from-sidebar-primary to-teal-600 text-white p-3 flex items-center gap-3">
            {/* Leave balance — hero card */}
            <div className="relative overflow-hidden">
                <div className="absolute -right-6 -top-6 w-12 h-12 rounded-full bg-white/10" />
                <div className="absolute -right-2 bottom-2 opacity-20">
                    <PlaneTakeoff className="w-16 h-16" />
                </div>
                <div className="relative">
                    <p className="text-xs font-semibold uppercase tracking-wide text-white/80 flex items-center gap-1.5">
                        <Sparkles className="w-3.5 h-3.5" /> Leave Balance
                    </p>
                    {balanceValue == null ? (
                        <p className="text-2xl font-bold mt-2">—</p>
                    ) : (
                        <div className="mt-1 flex items-end gap-1.5">
                            <span className="text-3xl font-bold leading-none">{balanceValue}</span>
                            <span className="text-xs text-white/80 mb-1">days available</span>
                        </div>
                    )}
                </div>
            </div>
            <button
                type="button"
                onClick={onExpand}
                title={pendingCount > 0 ? `Open panel — ${pendingCount} pending request${pendingCount > 1 ? "s" : ""}` : "Open panel"}
                aria-label={pendingCount > 0 ? `Open panel, ${pendingCount} pending requests` : "Open panel"}
                className="relative ml-auto shrink-0 h-8 w-8 inline-flex items-center justify-center rounded-lg bg-white/15 hover:bg-white/25 text-white transition-colors"
            >
                <ChevronsLeft className="w-4 h-4" />
                {pendingCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75 animate-ping" />
                        <span className="relative min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full bg-orange-500 text-white text-[10px] font-bold leading-none ring-2 ring-white/80 shadow">
                            {pendingCount > 9 ? "9+" : pendingCount}
                        </span>
                    </span>
                )}
            </button>
        </div>
    )
}

const SidePanel = ({ cursor, leaveBalance, pendingRequests, events, onNavigateDate, goTo }) => {
    const balanceValue = useMemo(() => {
        if (leaveBalance == null) return null;
        if (typeof leaveBalance === "number") return leaveBalance;
        if (typeof leaveBalance === "object") {
            const c = leaveBalance.balance ?? leaveBalance.available_leaves ?? leaveBalance.available;
            if (typeof c === "number") return c;
        }
        return null;
    }, [leaveBalance]);

    const stats = useMemo(() => {
        const counts = {
            leave_approved: 0, leave_pending: 0, wfh: 0, on_duty: 0,
            holiday: 0, overtime: 0, reimbursement: 0, advance: 0,
        };
        for (const e of events) {
            if (cursor && !dayjs(e.start_date).isSame(cursor, "month")) continue;
            if (counts[e.type] !== undefined) counts[e.type] += 1;
        }
        return counts;
    }, [events, cursor]);

    const upcomingHolidays = useMemo(() => {
        const today = dayjs().startOf("day");
        return events
            .filter((e) => {
                if (e.type !== "holiday") return false;
                const d = dayjs(e.start_date);
                return d.isSame(today, "day") || d.isAfter(today, "day");
            })
            .sort((a, b) => dayjs(a.start_date).valueOf() - dayjs(b.start_date).valueOf())
            .slice(0, 4);
    }, [events]);

    const todaysPlan = useMemo(() => {
        const today = dayjs();
        return events.filter((e) => {
            const s = dayjs(e.start_date);
            const en = dayjs(e.end_date || e.start_date);
            return (today.isSame(s, "day") || today.isAfter(s, "day")) &&
                (today.isSame(en, "day") || today.isBefore(en, "day"));
        });
    }, [events]);

    return (
        <>
            {/* Leave balance — hero card */}
            <div className="relative overflow-hidden rounded-2xl border border-sidebar-primary/20 shadow-sm bg-gradient-to-br from-sidebar-primary to-teal-600 text-white p-4">
                <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-white/10" />
                <div className="absolute -right-2 bottom-2 opacity-20">
                    <PlaneTakeoff className="w-16 h-16" />
                </div>
                <div className="relative">
                    <p className="text-xs font-semibold uppercase tracking-wide text-white/80 flex items-center gap-1.5">
                        <Sparkles className="w-3.5 h-3.5" /> Leave Balance
                    </p>
                    {balanceValue == null ? (
                        <p className="text-2xl font-bold mt-2">—</p>
                    ) : (
                        <div className="mt-1 flex items-end gap-1.5">
                            <span className="text-4xl font-bold leading-none">{balanceValue}</span>
                            <span className="text-xs text-white/80 mb-1">days available</span>
                        </div>
                    )}
                </div>
            </div>

            {/* This month snapshot */}
            <PanelCard icon={CalendarDays} title="This Month" iconTone="text-sidebar-primary">
                <div className="grid grid-cols-2 gap-2">
                    <StatTile color="red" label="Leaves" value={stats.leave_approved} />
                    <StatTile color="orange" label="Pending" value={stats.leave_pending} />
                    <StatTile color="green" label="Holidays" value={stats.holiday} />
                    <StatTile color="indigo" label="WFH" value={stats.wfh} />
                    <StatTile color="yellow" label="Overtime" value={stats.overtime} />
                    <StatTile color="cyan" label="Claims" value={stats.reimbursement + stats.advance} />
                </div>
            </PanelCard>

            {/* Today's plan */}
            {todaysPlan.length > 0 && (
                <PanelCard icon={Clock} title="Today's Plan" iconTone="text-teal-500" badge={todaysPlan.length} badgeTone="teal">
                    <ul className="space-y-1.5 max-h-52 overflow-auto -mr-1 pr-1">
                        {todaysPlan.map((e, i) => (
                            <PanelRow key={`${e.id}-${i}`} onClick={() => onNavigateDate(dayjs(e.start_date))} dot={EVENT_TYPES[e.type]?.dot}
                                title={e.title}
                                subtitle={`${EVENT_TYPES[e.type]?.label || ""}${e.description ? ` • ${e.description}` : ""}`}
                            />
                        ))}
                    </ul>
                </PanelCard>
            )}

            {/* Upcoming holidays */}
            {upcomingHolidays.length > 0 && (
                <PanelCard icon={Info} title="Upcoming Holidays" iconTone="text-green-500">
                    <ul className="space-y-2">
                        {upcomingHolidays.map((h) => (
                            <li
                                key={h.id}
                                onClick={() => onNavigateDate(dayjs(h.start_date))}
                                className="flex items-center gap-3 p-2 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-900/50 cursor-pointer transition-colors"
                            >
                                <div className="flex flex-col items-center justify-center w-11 h-11 rounded-xl bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 shrink-0">
                                    <span className="text-[10px] font-semibold uppercase leading-none">{dayjs(h.start_date).format("MMM")}</span>
                                    <span className="text-base font-bold leading-none mt-0.5">{dayjs(h.start_date).format("D")}</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{h.title}</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                        {dayjs(h.start_date).format("dddd")}
                                        {h.description ? ` • ${h.description}` : ""}
                                    </p>
                                </div>
                            </li>
                        ))}
                    </ul>
                </PanelCard>
            )}

            {/* Pending requests */}
            <PanelCard icon={AlertCircle} title="Pending Requests" iconTone="text-orange-500"
                badge={pendingRequests.length || undefined} badgeTone="orange">
                {pendingRequests.length === 0 ? (
                    <div className="text-center py-5">
                        <CheckCircle2 className="w-8 h-8 mx-auto text-green-400/70 mb-1.5" />
                        <p className="text-xs text-gray-400">All caught up — no pending requests</p>
                    </div>
                ) : (
                    <ul className="space-y-1.5 max-h-64 overflow-auto -mr-1 pr-1">
                        {pendingRequests.map((r, i) => (
                            <PanelRow key={`${r.id}-${i}`} onClick={() => onNavigateDate(dayjs(r.start_date))} dot={EVENT_TYPES[r.type]?.dot}
                                title={r.title}
                                subtitle={`${dayjs(r.start_date).format("MMM D")}${r.employee_name ? ` • ${r.employee_name}` : ""}`}
                                tag={EVENT_TYPES[r.type]?.label}
                            />
                        ))}
                    </ul>
                )}
            </PanelCard>
        </>
    );
};

/* ---- Small presentational helpers ---- */
const PanelCard = ({ icon: Icon, title, iconTone, badge, badgeTone = "teal", children }) => {
    const badgeTones = {
        teal: "bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300",
        orange: "bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300",
    };
    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-4">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 flex items-center gap-2 mb-3">
                <Icon className={cn("w-4 h-4", iconTone)} /> {title}
                {badge !== undefined && (
                    <span className={cn("ml-auto text-xs px-2 py-0.5 rounded-full font-semibold", badgeTones[badgeTone])}>{badge}</span>
                )}
            </h3>
            {children}
        </div>
    );
};

const PanelRow = ({ onClick, dot, title, subtitle, tag }) => (
    <li
        onClick={onClick}
        className="flex items-start gap-2.5 p-2 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-900/50 cursor-pointer transition-colors"
    >
        <span className={cn("w-2 h-2 mt-1.5 rounded-full shrink-0 ring-2 ring-white dark:ring-gray-800", dot)} />
        <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{title}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{subtitle}</p>
        </div>
        {tag && (
            <span className="text-[10px] uppercase font-semibold text-orange-600 dark:text-orange-400 mt-1 shrink-0">{tag}</span>
        )}
    </li>
);

const StatTile = ({ color, label, value }) => {
    const tones = {
        red: "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300",
        orange: "bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300",
        green: "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300",
        indigo: "bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300",
        purple: "bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300",
        cyan: "bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-300",
        yellow: "bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300",
    };
    return (
        <div className={cn("rounded-xl p-2.5 flex flex-col gap-0.5", tones[color])}>
            <span className="text-xl font-bold leading-none">{value}</span>
            <span className="text-[11px] uppercase tracking-wide font-medium opacity-80 truncate">{label}</span>
        </div>
    );
};

const QuickAction = ({ icon: Icon, label, onClick, disabled }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        title={disabled ? "Coming soon" : label}
        className={cn(
            "flex items-center gap-2 p-2.5 rounded-xl border transition-all text-xs font-medium",
            disabled
                ? "opacity-50 cursor-not-allowed border-gray-200 dark:border-gray-700 text-gray-500"
                : "border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-sidebar-primary hover:bg-sidebar-primary/5 hover:text-sidebar-primary hover:-translate-y-0.5"
        )}
    >
        <Icon className="w-4 h-4 text-sidebar-primary" />
        <span className="truncate">{label}</span>
    </button>
);

/* =====================================================
   DATE DRAWER — day detail sidebar
===================================================== */

const DrawerSection = ({ title, children, className }) => (
    <section className={cn("space-y-2.5", className)}>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-0.5">
            {title}
        </h3>
        {children}
    </section>
);

const DayStatusPill = ({ tone, children }) => {
    const tones = {
        green: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800/50",
        red: "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800/50",
        blue: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800/50",
        teal: "bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-950/40 dark:text-teal-300 dark:border-teal-800/50",
        gray: "bg-gray-50 text-gray-600 border-gray-200 dark:bg-gray-800/80 dark:text-gray-300 dark:border-gray-700",
        rose: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-800/50",
        amber: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800/50",
    };
    return (
        <span className={cn("inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium", tones[tone])}>
            {children}
        </span>
    );
};

const AttendanceDayCard = ({ event, hidePenaltyHints }) => {
    const m = event?.meta || {};
    const shiftIn = fmtTime(m.shift_check_in);
    const shiftOut = fmtTime(m.shift_check_out);
    const inT = fmtTime(m.check_in);
    const outT = fmtTime(m.check_out);

                            return (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40 overflow-hidden">
            <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-100 dark:border-gray-800">
                <div className="flex items-center gap-2 min-w-0">
                    <span className="w-2 h-2 rounded-full bg-teal-500 shrink-0" />
                    <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                        {event.title || "Present"}
                    </span>
                </div>
                {event.description && (
                    <span className="text-xs text-muted-foreground shrink-0">{event.description}</span>
                )}
            </div>
            <div className="px-4 py-3 space-y-2.5">
                {(shiftIn || shiftOut) && (
                    <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Shift</span>
                        <span className="font-medium tabular-nums text-gray-900 dark:text-gray-100">
                            {shiftIn || "—"} – {shiftOut || "—"}
                                                </span>
                    </div>
                                            )}
                {(inT || outT || m.check_in || m.check_out) && (
                    <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Punch</span>
                        <span className="font-medium tabular-nums text-gray-900 dark:text-gray-100">
                            {inT || "—"} · {outT || "—"}
                                        </span>
                                    </div>
                )}
                {!hidePenaltyHints && m.is_short_duty && (
                    <DayStatusPill tone="amber">
                        <Zap className="w-3 h-3" /> Short duty flagged
                    </DayStatusPill>
                )}
            </div>
                                </div>
                            );
};

const PenaltyDayCard = ({ event, onRaiseConcern }) => {
    const pm = penaltyMeta(event);
    const appeal = pm.appeal;

    return (
        <div className="rounded-xl border border-rose-200/80 dark:border-rose-900/50 bg-rose-50/50 dark:bg-rose-950/20 overflow-hidden">
            <div className="px-4 py-3 border-b border-rose-100 dark:border-rose-900/40">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <p className="text-xs font-medium text-rose-600/90 dark:text-rose-300 uppercase tracking-wide">
                            {pm.penalty_type}
                        </p>
                        <p className="text-2xl font-bold text-rose-700 dark:text-rose-200 mt-0.5 tabular-nums">
                            {pm.value_display}
                        </p>
                        <p className="text-xs text-rose-600/70 dark:text-rose-400/80 mt-1">
                            {pm.penalty_label} · {pm.review_status}
                        </p>
                    </div>
                    {appeal && (
                        <span className={cn(
                            "shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide",
                            APPEAL_STATUS_CHIP[appeal.status] || APPEAL_STATUS_CHIP.pending,
                        )}>
                            {appeal.status_label}
                        </span>
                )}
            </div>
                </div>
            {(event.description || appeal?.admin_comment) && (
                <div className="px-4 py-2.5 text-xs text-gray-600 dark:text-gray-400 space-y-1 border-b border-rose-100/80 dark:border-rose-900/30">
                    {event.description && <p>{event.description}</p>}
                    {appeal?.admin_comment && (
                        <p className="text-muted-foreground">
                            <span className="font-medium text-gray-700 dark:text-gray-300">HR note:</span> {appeal.admin_comment}
                        </p>
                    )}
            </div>
            )}
            {pm.can_appeal && onRaiseConcern && (
                <div className="px-4 py-3">
                    <Button
                        variant="outline"
                        size="sm"
                        className="w-full border-rose-300 text-rose-700 hover:bg-rose-100/80 dark:border-rose-800 dark:text-rose-200 dark:hover:bg-rose-900/40"
                        onClick={() => onRaiseConcern(pm)}
                    >
                        <MessageSquarePlus className="w-4 h-4 mr-2" />
                        Raise penalty concern
                </Button>
            </div>
            )}
            {appeal && !pm.can_appeal && appeal.status === "pending" && (
                <div className="px-4 py-3 text-xs text-amber-700 dark:text-amber-300 bg-amber-50/80 dark:bg-amber-950/30">
                    Your appeal is under HR review.
                </div>
            )}
        </div>
    );
};

const ScheduleEventRow = ({ event }) => {
    const t = EVENT_TYPES[event.type];
    return (
        <div className="flex items-start gap-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/30 px-3 py-2.5">
            <span className={cn("w-2 h-2 rounded-full mt-1.5 shrink-0", t?.dot)} />
            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{event.title}</p>
                    {needsStatusBadge(event) && (
                        <span className={cn(
                            "text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded-full border shrink-0",
                            STATUS_PILL[event.status],
                        )}>
                            {statusChipText(event.status)}
                        </span>
                    )}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{t?.label}</p>
                    {event.description && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{event.description}</p>
                    )}
                    <EventMeta ev={event} className="mt-1.5" />
                    <AttendanceDetail ev={event} className="mt-1.5" />
            </div>
        </div>
    );
};

const QuickActionLink = ({ icon: Icon, label, disabled, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={cn(
            "flex items-center gap-2.5 w-full rounded-xl border px-3 py-2.5 text-left text-sm transition-colors",
            disabled
                ? "opacity-45 cursor-not-allowed border-gray-200 dark:border-gray-800 text-gray-400"
                : "border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:border-sidebar-primary/40 hover:bg-sidebar-primary/5",
        )}
    >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-sidebar-primary/10 text-sidebar-primary">
            <Icon className="w-4 h-4" />
        </span>
        <span className="font-medium truncate">{label}</span>
    </button>
);

const DateDrawer = ({ date, events, onClose, goTo, onRaiseConcern, canSubmitShiftChange, onShiftChangeRequest }) => {
    if (!date) return null;

    const isToday = date.isSame(dayjs(), "day");
    const holidayEvent = events.find((e) => e.type === "holiday");
    const isHoliday = Boolean(holidayEvent);
    const isWeekOff = events.some((e) => e.type === "weekoff");
    const onLeave = events.some((e) => e.type === "leave_approved");
    const attendance = events.find((e) => e.type === "attendance");
    const penalties = events.filter((e) => e.type === "penalty");
    const overtimeEvents = events.filter((e) => e.type === "overtime");
    const shiftChangeEvents = events.filter((e) => e.type === "shift_change");
    const scheduleEvents = events.filter((e) => !["attendance", "penalty"].includes(e.type));

    const dateKey = date.format("YYYY-MM-DD");

    return (
        <div className="flex flex-col h-full max-h-[100dvh]">
            {/* Header */}
            <div className="shrink-0 px-5 pt-5 pb-4 border-b border-gray-200 dark:border-gray-800 bg-gray-50/80 dark:bg-gray-900/50">
                <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="flex flex-col items-center justify-center w-14 h-14 rounded-2xl bg-sidebar-primary text-white shrink-0 shadow-sm">
                            <span className="text-[10px] font-semibold uppercase tracking-wide opacity-90">{date.format("MMM")}</span>
                            <span className="text-xl font-bold leading-none">{date.format("D")}</span>
                        </div>
                        <div className="min-w-0 flex-1">
                            <SheetTitle className="text-lg font-semibold text-gray-900 dark:text-white leading-tight">
                                {date.format("dddd")}
                            </SheetTitle>
                            <SheetDescription className="text-sm text-muted-foreground mt-0.5">
                                {date.format("MMMM D, YYYY")}
                                {isToday && (
                                    <span className="ml-2 inline-flex items-center rounded-full bg-sidebar-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sidebar-primary">
                                        Today
                                    </span>
                                )}
                            </SheetDescription>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="h-8 w-8 shrink-0 inline-flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-200/60 dark:hover:bg-gray-800 dark:hover:text-gray-200 transition-colors"
                        aria-label="Close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Compact day status — single row, no stacked banners */}
                <div className="flex flex-wrap gap-1.5 mt-4">
                    {isHoliday && <DayStatusPill tone="green">{holidayEvent.title || "Holiday"}</DayStatusPill>}
                    {isWeekOff && <DayStatusPill tone="gray">Week off</DayStatusPill>}
                    {onLeave && <DayStatusPill tone="red">On leave</DayStatusPill>}
                    {attendance && !onLeave && !isHoliday && (
                        <DayStatusPill tone="teal">{attendance.title || "Present"}</DayStatusPill>
                    )}
                    {penalties.length > 0 && (
                        <DayStatusPill tone="rose">
                            {penalties.length} penalt{penalties.length > 1 ? "ies" : "y"}
                        </DayStatusPill>
                    )}
                    {overtimeEvents.length > 0 && (
                        <DayStatusPill tone="amber">Overtime</DayStatusPill>
                    )}
                    {shiftChangeEvents.length > 0 && (
                        <DayStatusPill tone="blue">
                            Shift change · {statusChipText(shiftChangeEvents[0].status) || "Requested"}
                        </DayStatusPill>
                    )}
                    {!isHoliday && !isWeekOff && !onLeave && !attendance && scheduleEvents.length === 0 && penalties.length === 0 && (
                        <DayStatusPill tone="gray">No records</DayStatusPill>
                    )}
                </div>
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
                {attendance && (
                    <DrawerSection title="Attendance">
                        <AttendanceDayCard event={attendance} hidePenaltyHints={penalties.length > 0} />
                    </DrawerSection>
                )}

                {penalties.length > 0 && (
                    <DrawerSection title="Penalties & deductions">
                        <div className="space-y-3">
                            {penalties.map((ev) => (
                                <PenaltyDayCard key={ev.id} event={ev} onRaiseConcern={onRaiseConcern} />
                            ))}
                        </div>
                    </DrawerSection>
                )}

                {scheduleEvents.length > 0 && (
                    <DrawerSection title="Schedule">
                        <div className="space-y-2">
                            {scheduleEvents.map((ev, i) => (
                                <ScheduleEventRow key={`${ev.id}-${i}`} event={ev} />
                            ))}
                        </div>
                    </DrawerSection>
                )}

                {!attendance && penalties.length === 0 && scheduleEvents.length === 0 && (
                    <div className="rounded-xl border border-dashed border-gray-200 dark:border-gray-700 py-10 text-center">
                        <CalendarDays className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600 mb-2" />
                        <p className="text-sm text-muted-foreground">Nothing scheduled for this day</p>
                    </div>
                )}

                <DrawerSection title="Quick actions">
                    <div className="grid grid-cols-1 gap-2">
                        <QuickActionLink
                            icon={PlaneTakeoff}
                            label="Apply leave"
                            disabled={isHoliday || isWeekOff || onLeave}
                            onClick={() => goTo("leave", { prefillDate: dateKey })}
                        />
                        <QuickActionLink
                            icon={HomeIcon}
                            label="Work from home"
                            disabled={isHoliday || isWeekOff}
                            onClick={() => goTo("wfh", { prefillDate: dateKey, leave_request_type: "work_from_home" })}
                        />
                        {/* <QuickActionLink
                            icon={MapPin}
                            label="On duty request"
                            onClick={() => goTo("on_duty", { prefillDate: dateKey, leave_request_type: "on_duty" })}
                        /> */}
                        {canSubmitShiftChange && (
                            <QuickActionLink
                                icon={ArrowLeftRight}
                                label="Shift change request"
                                disabled={isHoliday || onLeave}
                                onClick={() => onShiftChangeRequest?.(date, events)}
                            />
                        )}
                        <QuickActionLink
                            icon={Receipt}
                            label="Reimbursement"
                            onClick={() => goTo("reimbursement", { prefillDate: dateKey })}
                        />
                        <QuickActionLink
                            icon={HandCoins}
                            label="Advance salary"
                            onClick={() => goTo("advance")}
                        />
                    </div>
                </DrawerSection>
            </div>
        </div>
    );
};

export default Calendar;
